import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { apiService } from '../services/api';
import type { CrmCampaignRef, CrmTerritorialHeatPoint, CrmTerritorialOverviewResponse } from '../types/crm';

const periodOptions = [
  { label: 'Últimos 7 dias', value: 7 },
  { label: 'Últimos 14 dias', value: 14 },
  { label: 'Últimos 30 dias', value: 30 },
  { label: 'Últimos 60 dias', value: 60 }
];

const channels = [
  { label: 'Todos os canais', value: '' },
  { label: 'WhatsApp', value: 'WHATSAPP' },
  { label: 'E-mail', value: 'EMAIL' }
];

const formatPercent = (value: number) => `${Math.round(value)}%`;
const formatNumber = (value: number) => new Intl.NumberFormat('pt-BR').format(value);

function territoryHeatStyle(point: CrmTerritorialHeatPoint) {
  if (point.heatLevel === 'HIGH') return 'bg-rose-50 border-rose-200 text-rose-900';
  if (point.heatLevel === 'MEDIUM') return 'bg-amber-50 border-amber-200 text-amber-900';
  return 'bg-slate-50 border-slate-200 text-slate-900';
}

export function CrmTerritorialPage() {
  const [overview, setOverview] = useState<CrmTerritorialOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTerritory, setSelectedTerritory] = useState<CrmTerritorialHeatPoint | null>(null);
  const [filters, setFilters] = useState({ periodDays: 30, campaignId: '', channel: '' });
  const navigate = useNavigate();
  const location = useLocation();

  const selectedPoint = useMemo(() => {
    if (selectedTerritory) return selectedTerritory;
    return overview?.heatmap?.[0] || null;
  }, [overview, selectedTerritory]);

  const loadOverview = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTerritorialOverview({
        periodDays: filters.periodDays,
        campaignId: filters.campaignId || undefined,
        channel: filters.channel || undefined
      });
      setOverview(response.data);
      setSelectedTerritory(null);
    } catch (error) {
      console.error('Erro ao carregar visão territorial:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, [filters]);

  const trendBars = useMemo(() => {
    return overview?.evolution.map((point) => ({
      label: point.day,
      score: point.score,
      whatsappRead: point.whatsappRead,
      emailOpened: point.emailOpened,
      responses: point.responses
    })) || [];
  }, [overview]);

  return (
    <>
      <Header
        title="Territórios"
        subtitle="Visão política por bairros, regiões e campanhas para priorizar engajamento."
        actions={location.pathname === '/crm/territorial' ? (
          <button
            onClick={() => navigate('/crm')}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            Voltar ao CRM
          </button>
        ) : undefined}
      />

      <div className="space-y-6 p-6">
        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-semibold text-slate-500">Territórios ativos</p>
            <p className="mt-3 text-3xl font-semibold text-slate-900">{formatNumber(overview?.summary.totalTerritories || 0)}</p>
            <p className="mt-2 text-sm text-slate-500">Regiões classificadas por potencial político.</p>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-semibold text-slate-500">Contatos no período</p>
            <p className="mt-3 text-3xl font-semibold text-slate-900">{formatNumber(overview?.summary.totalContacts || 0)}</p>
            <p className="mt-2 text-sm text-slate-500">Bases dentro do comando de engajamento.</p>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-semibold text-slate-500">Mensagens enviadas</p>
            <p className="mt-3 text-3xl font-semibold text-slate-900">{formatNumber(overview?.summary.totalSent || 0)}</p>
            <p className="mt-2 text-sm text-slate-500">WhatsApp + e-mail.</p>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-semibold text-slate-500">Respostas recebidas</p>
            <p className="mt-3 text-3xl font-semibold text-slate-900">{formatNumber(overview?.summary.totalResponses || 0)}</p>
            <p className="mt-2 text-sm text-slate-500">Engajamento real em campo.</p>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-950">Filtros de inteligência</h2>
              <p className="mt-1 text-sm text-slate-500">Refine por campanha, canal e janela de análise.</p>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <select
                value={filters.periodDays}
                onChange={(event) => setFilters((prev) => ({ ...prev, periodDays: Number(event.target.value) }))}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm"
              >
                {periodOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>

              <select
                value={filters.channel}
                onChange={(event) => setFilters((prev) => ({ ...prev, channel: event.target.value }))}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm"
              >
                {channels.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>

              <select
                value={filters.campaignId}
                onChange={(event) => setFilters((prev) => ({ ...prev, campaignId: event.target.value }))}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm"
              >
                <option value="">Todas as campanhas</option>
                {overview?.availableCampaigns.map((campaign: CrmCampaignRef) => (
                  <option key={campaign.id} value={campaign.id}>{campaign.nome}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-950">Mapa territorial</h2>
              <p className="mt-1 text-sm text-slate-500">Bairros com maior potencial de mobilização e resposta.</p>
            </div>
            <div className="text-sm text-slate-500">{overview ? `Período: ${overview.period.days} dias (${overview.period.start} até ${overview.period.end})` : 'Carregando...'}</div>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {(loading ? Array.from({ length: 6 }) : overview?.heatmap || []).map((point, index) => {
              if (loading) {
                return (
                  <div key={index} className="h-36 animate-pulse rounded-3xl border border-slate-200 bg-slate-100" />
                );
              }

              return (
                <button
                  key={`${point.bairro}-${point.cidade}-${point.regiao}-${index}`}
                  onClick={() => setSelectedTerritory(point)}
                  className={`group rounded-3xl border p-5 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg ${territoryHeatStyle(point)} ${selectedPoint?.bairro === point.bairro && selectedPoint?.cidade === point.cidade ? 'ring-2 ring-slate-900/10 shadow-lg' : ''}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold">{point.bairro}</p>
                      <p className="text-xs text-slate-600">{point.regiao} · {point.cidade}</p>
                    </div>
                    <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-900 shadow-sm">{point.heatLevel}</span>
                  </div>

                  <div className="mt-4 space-y-2 text-sm text-slate-700">
                    <div className="flex items-center justify-between">
                      <span>Contatos</span>
                      <span className="font-semibold">{formatNumber(point.contactsCount)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Score</span>
                      <span className="font-semibold">{formatNumber(point.score)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Campanhas</span>
                      <span className="font-semibold">{formatNumber(point.campaignsCount)}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.4fr_0.8fr]">
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Comparativo de campanhas</h2>
                <p className="mt-1 text-sm text-slate-500">Ranking de ações com maior leitura, clique e resposta.</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Campanha</th>
                    <th className="px-4 py-3">Enviadas</th>
                    <th className="px-4 py-3">Abertas</th>
                    <th className="px-4 py-3">Cliques</th>
                    <th className="px-4 py-3">Respostas</th>
                    <th className="px-4 py-3">Score</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {overview?.campaignComparison.map((campaign) => (
                    <tr key={campaign.campaignId}>
                      <td className="px-4 py-4 font-semibold text-slate-900">{campaign.campaignName}</td>
                      <td className="px-4 py-4 text-slate-600">{formatNumber(campaign.sent)}</td>
                      <td className="px-4 py-4 text-slate-600">{formatNumber(campaign.opens)}</td>
                      <td className="px-4 py-4 text-slate-600">{formatNumber(campaign.clicks)}</td>
                      <td className="px-4 py-4 text-slate-600">{formatNumber(campaign.responses)}</td>
                      <td className="px-4 py-4 text-slate-600">{formatNumber(campaign.score)}</td>
                    </tr>
                  ))}
                  {overview?.campaignComparison.length === 0 && !loading ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-slate-400">Nenhuma campanha disponível.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <div className="mt-6 rounded-3xl border border-slate-100 bg-slate-50 p-4">
              <div className="flex items-center justify-between text-sm text-slate-700">
                <span>Maior bairro</span>
                <span className="font-semibold text-slate-900">{overview?.summary.topNeighborhood || 'Não definido'}</span>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm text-slate-700">
                <span>Menor bairro</span>
                <span className="font-semibold text-slate-900">{overview?.summary.lowNeighborhood || 'Não definido'}</span>
              </div>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-950">Perfil do território</h2>
              <p className="mt-1 text-sm text-slate-500">Métrica detalhada do bairro selecionado.</p>

              <div className="mt-5 space-y-3 text-sm text-slate-700">
                <div className="rounded-3xl bg-slate-50 p-4">
                  <p className="text-sm text-slate-500">Bairro</p>
                  <p className="mt-2 font-semibold text-slate-900">{selectedPoint?.bairro || 'Nenhum selecionado'}</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-3xl bg-slate-50 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Score médio</p>
                    <p className="mt-2 text-2xl font-semibold text-slate-900">{selectedPoint ? formatNumber(selectedPoint.score) : '—'}</p>
                  </div>
                  <div className="rounded-3xl bg-slate-50 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Contatos</p>
                    <p className="mt-2 text-2xl font-semibold text-slate-900">{selectedPoint ? formatNumber(selectedPoint.contactsCount) : '—'}</p>
                  </div>
                </div>

                <div className="rounded-3xl bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">Leitura e resposta</p>
                  <div className="mt-3 space-y-2 text-xs text-slate-600">
                    <div className="flex items-center justify-between">
                      <span>WhatsApp lidos</span>
                      <span>{selectedPoint ? formatPercent(selectedPoint.whatsappReadRate) : '—'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>E-mails abertos</span>
                      <span>{selectedPoint ? formatPercent(selectedPoint.emailOpenRate) : '—'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Taxa de resposta</span>
                      <span>{selectedPoint ? formatPercent(selectedPoint.responseRate) : '—'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-950">Insights rápidos</h2>
              <p className="mt-1 text-sm text-slate-500">Sugestões automáticas para priorizar ação.</p>
              <ul className="mt-4 space-y-3 text-sm text-slate-700">
                {overview?.insights.map((insight, index) => (
                  <li key={index} className="rounded-2xl bg-slate-50 p-3">{insight}</li>
                ))}
                {overview?.insights.length === 0 && !loading ? (
                  <li className="rounded-2xl bg-slate-50 p-3 text-slate-500">Nenhum insight gerado para o período.</li>
                ) : null}
              </ul>
            </div>
          </aside>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-950">Evolução da campanha</h2>
              <p className="mt-1 text-sm text-slate-500">Tendência de leitura, resposta e score por dia.</p>
            </div>
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {trendBars.map((point) => (
              <div key={point.label} className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-900">{point.label}</p>
                <div className="mt-4 h-32 overflow-hidden rounded-3xl bg-white p-3">
                  <div className="flex h-full flex-col justify-end gap-2">
                    <div className="h-[18%] rounded-full bg-slate-300" style={{ height: `${Math.min(point.score, 100)}%` }}>
                      <span className="sr-only">Score {point.score}</span>
                    </div>
                    <div className="h-[14%] rounded-full bg-emerald-300" style={{ height: `${Math.min(point.whatsappRead, 100)}%` }}>
                      <span className="sr-only">Leitura {point.whatsappRead}</span>
                    </div>
                    <div className="h-[12%] rounded-full bg-cyan-300" style={{ height: `${Math.min(point.emailOpened, 100)}%` }}>
                      <span className="sr-only">Abertura {point.emailOpened}</span>
                    </div>
                    <div className="h-[10%] rounded-full bg-indigo-300" style={{ height: `${Math.min(point.responses, 100)}%` }}>
                      <span className="sr-only">Respostas {point.responses}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-sm text-slate-600">
                  <div className="flex justify-between"><span>Score</span><span>{formatNumber(point.score)}</span></div>
                  <div className="flex justify-between"><span>Leitura</span><span>{formatNumber(point.whatsappRead)}</span></div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}
